import { Issue, UserProfile } from "../types";

export interface TicketNotificationItem {
  issueId: string;
  issueTitle: string;
  appId: string;
  appName: string;
  authorName: string;
  authorEmail: string;
  commentPreview: string;
  timestamp: string;
  isUnread: boolean;
}

const STORAGE_PREFIX = "mlc_read_tickets_";

/**
 * Safely reads the stored read-receipt map for a given user from localStorage.
 */
export function getReadReceipts(userUid: string): Record<string, string> {
  if (typeof window === "undefined" || !userUid) return {};
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${userUid}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Gets the timestamp when a user last viewed/read a specific ticket.
 */
export function getTicketLastReadTime(
  issueId: string,
  userUid: string,
): string | null {
  const receipts = getReadReceipts(userUid);
  return receipts[issueId] || null;
}

/**
 * Marks a specific ticket as read for the user up to the current timestamp.
 */
export function markTicketAsRead(
  issueId: string,
  userUid: string,
  readAt: string = new Date().toISOString(),
): void {
  if (typeof window === "undefined" || !userUid || !issueId) return;
  try {
    const receipts = getReadReceipts(userUid);
    receipts[issueId] = readAt;
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${userUid}`,
      JSON.stringify(receipts),
    );
    window.dispatchEvent(
      new CustomEvent("mlc-ticket-read-updated", {
        detail: { issueId, userUid, readAt },
      }),
    );
  } catch (err: unknown) {
    console.warn("Could not save ticket read receipt:", err);
  }
}

/**
 * Marks all currently supplied issues as read for the user.
 */
export function markAllTicketsAsRead(
  issues: Issue[],
  userUid: string,
  readAt: string = new Date().toISOString(),
): void {
  if (typeof window === "undefined" || !userUid) return;
  try {
    const receipts = getReadReceipts(userUid);
    for (const issue of issues) {
      if (issue.id) {
        receipts[issue.id] = issue.lastCommentAt || issue.updatedAt || readAt;
      }
    }
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${userUid}`,
      JSON.stringify(receipts),
    );
    window.dispatchEvent(
      new CustomEvent("mlc-ticket-read-updated", {
        detail: { userUid, readAt },
      }),
    );
  } catch (err: unknown) {
    console.warn("Could not mark all tickets as read:", err);
  }
}

/**
 * Determines whether the user is eligible/interested to receive notifications for this issue.
 */
export function isUserEligibleForIssueNotifications(
  issue: Issue,
  currentUser: UserProfile,
): boolean {
  if (currentUser.role === "it_admin") return true;

  const normalizedUserEmail = (currentUser.email || "").toLowerCase().trim();
  const normalizedReporter = (issue.reporterEmail || "").toLowerCase().trim();
  const normalizedAssignee = (issue.assigneeEmail || "").toLowerCase().trim();

  // Assigned admin or original ticket reporter
  if (
    issue.assigneeUid === currentUser.uid ||
    normalizedAssignee === normalizedUserEmail ||
    issue.reporterUid === currentUser.uid ||
    normalizedReporter === normalizedUserEmail
  ) {
    return true;
  }

  // Dept admin for this application
  if (currentUser.role === "dept_admin") {
    const allowed = currentUser.allowedApps || [];
    if (
      allowed.includes("*") ||
      (issue.appId && allowed.includes(issue.appId)) ||
      (issue.appName && allowed.includes(issue.appName))
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Determines whether a specific issue has an unread response for the current user.
 */
export function hasUnreadResponse(
  issue: Issue,
  currentUser?: UserProfile | null,
): boolean {
  if (!currentUser || !currentUser.uid || !issue.lastCommentAt) return false;

  const normalizedUserEmail = (currentUser.email || "").toLowerCase().trim();
  const normalizedCommentAuthor = (issue.lastCommentAuthorEmail || "")
    .toLowerCase()
    .trim();

  // Author of the comment never receives unread notification for their own comment
  if (
    normalizedCommentAuthor &&
    normalizedCommentAuthor === normalizedUserEmail
  ) {
    return false;
  }

  // Check relevance to user
  if (!isUserEligibleForIssueNotifications(issue, currentUser)) {
    return false;
  }

  const lastRead = getTicketLastReadTime(issue.id, currentUser.uid);
  if (!lastRead) return true;

  const commentTime = new Date(issue.lastCommentAt).getTime();
  const readTime = new Date(lastRead).getTime();

  return commentTime > readTime;
}

/**
 * Calculates total count of tickets with unread responses for the current user.
 */
export function getUnreadResponseCount(
  issues: Issue[],
  currentUser?: UserProfile | null,
): number {
  if (!currentUser) return 0;
  return issues.filter((i) => hasUnreadResponse(i, currentUser)).length;
}

/**
 * Derives a sorted list of recent response notifications across all relevant tickets.
 */
export function getTicketNotifications(
  issues: Issue[],
  currentUser?: UserProfile | null,
): TicketNotificationItem[] {
  if (!currentUser) return [];

  const items: TicketNotificationItem[] = [];

  for (const issue of issues) {
    if (!issue.lastCommentAt) continue;

    const normalizedUserEmail = (currentUser.email || "").toLowerCase().trim();
    const normalizedCommentAuthor = (issue.lastCommentAuthorEmail || "")
      .toLowerCase()
      .trim();

    // Skip if current user posted the comment and there are no other unread items
    if (
      normalizedCommentAuthor &&
      normalizedCommentAuthor === normalizedUserEmail
    ) {
      continue;
    }

    if (!isUserEligibleForIssueNotifications(issue, currentUser)) {
      continue;
    }

    const isUnread = hasUnreadResponse(issue, currentUser);
    const authorName =
      issue.lastCommentAuthorName ||
      issue.lastCommentAuthorEmail?.split("@")[0] ||
      "Someone";
    const commentPreview = issue.lastCommentPreview || "New response on ticket";

    items.push({
      issueId: issue.id,
      issueTitle: issue.title || "Untitled Issue",
      appId: issue.appId,
      appName: issue.appName || issue.appId || "MLC Application",
      authorName,
      authorEmail: issue.lastCommentAuthorEmail || "",
      commentPreview,
      timestamp: issue.lastCommentAt,
      isUnread,
    });
  }

  // Sort newest first
  items.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return items;
}

/**
 * Formats a notification date string into a friendly relative or short date format.
 */
export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (isNaN(diffMs)) return dateString;

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
